// React
import { memo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

// MUI
import { CardActionArea, Checkbox, Divider, FormControlLabel, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { gridExpandedSortedRowEntriesSelector, useGridApiContext } from "@mui/x-data-grid-premium";

// Third-party
import { v4 as uuidv4 } from "uuid";

// Context
import { useSelectedValue } from "@/Motivators/context/AutoAlerts/SelectedValueContext";

// Components
import ActionCard from "@Layouts/components/Cards/ActionCard/ActionCard";
import MainDialogComponent from "@Layouts/components/Dialogs/MainDialog/index";
import Button from "@Layouts/components/Buttons/Button/index";

const LoadingTitle = () => <Skeleton variant="rectangular" width={280} height={25} className="!mt-[8px] rounded-[4px]" />;
const LoadingItems = (props) => {
	return (
		<>
			<Grid container alignContent={"flex-start"} spacing={3} className="!mt-0 !ml-0 pr-6">
				{Array.from(Array(8)).map(() => (
					<Grid key={uuidv4()} item xs={6} md={4}>
						<ActionCard
							classes={"pb-[5px] [&>.card-header-padding-top-5.MuiCardHeader-root]:pt-[2px]"}
							checkbox
							title
							content={props.showPosition || false}
							loading={true}
						/>
					</Grid>
				))}
			</Grid>
			<Stack
				direction={"row"}
				justifyContent={"center"}
				alignItems={"center"}
				sx={{ width: "100%", textAlign: "center" }}
				className="pt-8"
			>
				<Skeleton variant="rectangular" width={230} height={38} sx={{ borderRadius: "4px" }} />
			</Stack>
		</>
	);
};

LoadingItems.propTypes = {
	showPosition: PropTypes.bool,
};

const AutoAlertsMultiSelector = (props) => {
	const apiRef = useGridApiContext();
	const [checkedState, setCheckedState] = useState({});
	const {
		updateSectionTypes,
		selectedValue,
		checkedActions,
		setSelectedValue,
		setCheckedActions,
		setSelectedOptionNames,
		setAllActionTypes,
		setActivityStatusError,
		setActionStatusError,
	} = useSelectedValue();

	const updateCheckedState = (selectedValue = []) => {
		if (!selectedValue?.length) {
			return false;
		}
		const newState = {};
		selectedValue.forEach((item) => {
			newState[item._id] = true;
		});
		setCheckedState(newState);
	};

	useEffect(() => {
		if (props.id !== "activity") return;

		if (!Object.keys(checkedState).length && selectedValue && selectedValue?.length) {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			updateCheckedState(selectedValue);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedValue]);

	useEffect(() => {
		if (props.id !== "action") return;

		if (!Object.keys(checkedState).length && checkedActions && checkedActions?.length) {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			updateCheckedState(checkedActions);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkedActions]);

	useEffect(() => {
		if (props.id !== "action") return;

		if (updateSectionTypes) {
			updateCheckedState(updateSectionTypes);
		} else {
			updateCheckedState([]);
		}
	}, [props.id, updateSectionTypes]);

	const handleCheckboxChange = (_event, id, isDisabled = false) => {
		if (isDisabled) {
			return;
		}
		let selectedItems;

		if (props.multiple) {
			setCheckedState((prevState) => {
				selectedItems = { ...prevState, [id]: !prevState[id] };
				return selectedItems;
			});
		} else {
			selectedItems = { [id]: true };
			setCheckedState(selectedItems);
		}
	};

	useEffect(() => {
		const allSelectedIds = Object.keys(checkedState).filter((key) => checkedState[key]);
		const selectedOptions = props.options.filter((option) => allSelectedIds.includes(option._id));
		const allSelectedNames = selectedOptions.map((item) => item.description);

		const isEditing = (props?.propsId && apiRef.current.getCellMode(props.propsId, props.field)) || null;
		const cellValue = (props?.propsId && apiRef.current.getCellValue(props.propsId, props.field)) || null;

		switch (props.id) {
			case "activity":
				if (selectedOptions?.length) {
					setSelectedValue(selectedOptions);
					setSelectedOptionNames((prevOptionNames) => {
						return { ...prevOptionNames, [props.id]: allSelectedNames };
					});
					setActivityStatusError({ statusValue: false });
				} else if (checkedState && Object.keys(checkedState)?.length) {
					setSelectedValue(null);
					setSelectedOptionNames((prevOptionNames) => {
						return { ...prevOptionNames, [props.id]: null };
					});
					setActivityStatusError({ statusValue: true });

					// resetting the action multiselect
					setCheckedActions(null);
					setSelectedOptionNames((prevOptionNames) => {
						return { ...prevOptionNames, ["action"]: null };
					});
					setActionStatusError({ statusValue: true });
				} else if (isEditing && cellValue?.length && !checkedActions) {
					const filteredOptions = cellValue.reduce((acc, cell) => {
						const matches = props.options.filter((item) => item.description === cell.description);
						return acc.concat(matches);
					}, []);
					setSelectedValue(filteredOptions);
					setSelectedOptionNames((prevOptionNames) => {
						return { ...prevOptionNames, [props.id]: null };
					});
				}
				break;
			case "action":
				if (props.options) {
					setAllActionTypes(props.options);
				}
				if (selectedOptions?.length) {
					setCheckedActions(selectedOptions);
					setSelectedOptionNames((prevOptionNames) => {
						return { ...prevOptionNames, [props.id]: allSelectedNames };
					});

					setActionStatusError({ statusValue: false });
				} else if (checkedState && Object.keys(checkedState)?.length) {
					setCheckedActions(null);
					setSelectedOptionNames((prevOptionNames) => {
						return { ...prevOptionNames, [props.id]: null };
					});
					setActionStatusError({ statusValue: true });
				} else if (isEditing && cellValue?.length && !checkedActions) {
					const filteredOptions = cellValue.reduce((acc, cell) => {
						const matches = props.options.filter((item) => item.description === cell.description);
						return acc.concat(matches);
					}, []);
					setSelectedValue(filteredOptions);
					setSelectedOptionNames((prevOptionNames) => {
						return { ...prevOptionNames, [props.id]: null };
					});
					setActionStatusError({ statusValue: true });
				}
				break;
			default:
				break;
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkedState]);

	const disabled = !props.options || props.options.length === 0;

	if (disabled && !props.loading) {
		return (
			<Grid container direction="row" justify="center" alignItems="center">
				<Typography
					title={props.messageError}
					className={`regular-pt-sans-pro-narrow-label fs-15, auto-alerts-msg-error`}
					component="p"
				>
					{props.messageError}
				</Typography>
			</Grid>
		);
	}

	const handleSelectAll = (e, groupId) => {
		const checkValue = e.target.checked;
		const updatedState = { ...checkedState };

		// Find the correct group
		const group = props.groupedOptions.find((group) => {
			return Object.keys(group)[0] === groupId;
		});

		// Update the state for each option within the group
		if (group) {
			const optionsToUpdate = group[groupId];

			optionsToUpdate.forEach((option) => {
				updatedState[option._id] = checkValue;
			});
		}

		setCheckedState(updatedState);
	};

	return (
		<Grid container className="!max-h-[700px] !overflow-y-auto">
			{props.loading ? (
				<LoadingItems />
			) : (
				<Grid container alignContent={"flex-start"} spacing={3} className="!mt-0 !ml-0 pr-4 pb-3">
					{props.groupedOptions && props.groupedOptions.length > 0
						? props.groupedOptions.map((group, groupKey) => {
								const groupId = Object.keys(group)[0];
								return (
									<Grid item xs={12} key={uuidv4()}>
										<Stack ml={1.4} className="relative z-50 mb-[2px]">
											<FormControlLabel
												label={<Typography className={"regular-montserrat-label fs-18"}>{groupId}</Typography>}
												control={
													<Checkbox
														checked={group[groupId].every((option) => checkedState[option._id])}
														onChange={(e) => handleSelectAll(e, groupId)}
														indeterminate={Boolean(group[groupId].every((option) => checkedState[option._id]))}
													/>
												}
											/>
										</Stack>
										<Grid container spacing={3}>
											{group[groupId].map((option) => (
												<Grid key={option._id} item xs={6} md={4}>
													<CardActionArea
														disabled={false}
														className="!rounded-[4px]"
														onClick={(e) => handleCheckboxChange(e, option._id)}
													>
														<ActionCard
															checkbox
															title={
																<Typography
																	className={"regular-pt-sans-pro-narrow-label !text-[11pt]"}
																	component={"div"}
																>
																	{option.description}
																</Typography>
															}
															classes={
																"[&>.card-header-padding-top-5.MuiCardHeader-root]:pt-[4px] min-h-[32px]"
															}
															label={option.description}
															checked={checkedState[option._id] || false}
															{...option}
														/>
													</CardActionArea>
												</Grid>
											))}
										</Grid>
										{groupKey + 1 < props.groupedOptions.length && (
											<Divider className={"!mt-8 !mb-[-5px] !border-b !border-[#CCC]"} />
										)}
									</Grid>
								);
						  })
						: props.options?.map((option) => (
								<Grid item xs={6} md={4} key={option._id}>
									<CardActionArea
										disabled={option?.disabled || false}
										className="!rounded-[4px]"
										onClick={(e) => handleCheckboxChange(e, option?._id, option?.disabled)}
									>
										<ActionCard
											disabled={option?.disabled || false}
											checkbox
											title={
												<Typography className={"regular-pt-sans-pro-narrow-label !text-[11pt]"} component={"div"}>
													{option?.description || ""}
												</Typography>
											}
											classes={"[&>.card-header-padding-top-5.MuiCardHeader-root]:pt-[4px] min-h-[32px]"}
											label={option?.description}
											checked={checkedState[option?._id] || false}
											{...option}
										/>
									</CardActionArea>
								</Grid>
						  ))}
				</Grid>
			)}
		</Grid>
	);
};

const canSetActionType = (props) => Boolean(props.optionsData && props?.apiRef && props.optionsData?.length);

const formatNames = (values) => {
	if (values && values.length > 0) {
		if (values.length === 1) {
			return values[0];
		} else if (values.length === 2) {
			return `${values[0]}, ${values[1]}`;
		} else {
			return `${values[0]}, ${values[1]}, ${values.length - 2} more`;
		}
	}
	return "";
};

const updateActivityTypes = (listActionType, apiRef, propsId) => {
	const rows = gridExpandedSortedRowEntriesSelector(apiRef);
	let columnData = rows.map((row) => row.model.activityType);
	columnData = columnData.flat();

	let updatedActivityTypes = listActionType.map((activityType) => {
		const isDisabled = columnData.some((selectedOption) => selectedOption?._id === activityType._id);
		return { ...activityType, disabled: isDisabled };
	});

	// getting current editing row to re-enable it.
	const editState = apiRef.current.getRow(propsId);
	const currentActivityType = editState.activityType || [];

	// Check if a row is being edited
	if (currentActivityType.length > 0) {
		// re-enable current selections.
		updatedActivityTypes = updatedActivityTypes.map((activityType) => {
			const isFoundInCurrentActivityType = currentActivityType.some((selectedOption) => selectedOption._id === activityType._id);

			if (isFoundInCurrentActivityType) {
				return { ...activityType, disabled: false };
			}

			return activityType;
		});
	}

	return updatedActivityTypes;
};

const isAction = (props) => props.id === "action";

const hasErrors = (props) => props?.fieldStateError;

const evaluateTwoValues = (optionA, optionB) => optionA && optionB;

const isActionWithoutErrors = (props) => isAction(props) && !hasErrors(props);

const isSelectActionType = (props) => props?.linkName === "Select Action Types";

const getActionType = (actionType) => actionType || [];

const getMessageError = (props) => (props.messageError ? props.messageError : "No options to select.");

const isMultiple = (props) => (props.multiple ? props.multiple : false);

const getGroupedOptions = (groupedOptions) => groupedOptions || null;

const displayButton = ({ loading, description, actionType, setOpenDialog }) => {
	if (!loading) {
		return (
			<Button
				variant="contained"
				text={`Add ${description}`}
				classes="btn-regular-add btn-regular-text btn-pro-narrow"
				disabled={!actionType || actionType.length === 0}
				onClick={() => {
					setOpenDialog(false);
				}}
			/>
		);
	}

	return null;
};

const MultiSelectorLink = memo((props) => {
	const [openDialog, setOpenDialog] = useState(false);
	const [description, setDescription] = useState("");
	const [actionType, setActionType] = useState([]);
	const [groupedOptions, setGroupedOptions] = useState(null);

	useEffect(() => {
		if (!props.contextData?.length) {
			return;
		}

		const arrActionType = props.contextData.map((item) => item.actionType);
		setActionType(arrActionType.flat());

		if (isAction(props)) {
			setGroupedOptions(props.contextData.map((item) => ({ [item.description]: item.actionType })));
		}
	}, [props.contextData]);

	useEffect(() => {
		if (canSetActionType(props)) {
			setActionType(updateActivityTypes(props.optionsData, props.apiRef, props.propsId));
		}
	}, [props.optionsData]);

	useEffect(() => {
		if (props.modalTitle) {
			setDescription(props.modalTitle);
		}
	}, [props.modalTitle]);

	return (
		<>
			<Typography
				onClick={() => {
					if (evaluateTwoValues(isAction(props), hasErrors(props))) {
						setOpenDialog(false);
					} else {
						setOpenDialog(true);
					}
				}}
				className={clsx({
					"regular-pt-sans-pro-narrow-label": true,
					"fs-15": true,
					"!mt-[2px]": true,
					"empty-link-look-disabled": evaluateTwoValues(isAction(props), hasErrors(props)),
					"empty-link-look":
						evaluateTwoValues(!isAction(props), hasErrors(props)) ||
						evaluateTwoValues(isActionWithoutErrors(props), isSelectActionType(props)),
					"link-look":
						evaluateTwoValues(!isAction(props), !hasErrors(props)) ||
						evaluateTwoValues(isActionWithoutErrors(props), !isSelectActionType(props)),
				})}
				component={"span"}
			>
				{props.linkName ?? ""}
			</Typography>
			<MainDialogComponent
				openDialog={openDialog}
				onClose={() => setOpenDialog(false)}
				width={800}
				modalTitle={props.loading ? <LoadingTitle /> : description}
				titleClasses={"!h-[auto] !pb-2 !font-montserrat !text-[20px] text-default"}
			>
				<Grid container className="pb-6">
					<Grid item xs={12} className="pr-2">
						<AutoAlertsMultiSelector
							id={props.id}
							loading={props.loading}
							options={getActionType(actionType)}
							messageError={getMessageError(props)}
							multiple={isMultiple(props)}
							groupedOptions={getGroupedOptions(groupedOptions)}
							propsId={props.propsId}
							field={props.field}
						/>
					</Grid>
					{!props.loading && (
						<Grid item xs={12} style={{ display: "flex", justifyContent: "center" }} className="pt-6">
							{displayButton({
								description,
								actionType,
								setOpenDialog,
								loading: props.loading,
							})}
						</Grid>
					)}
				</Grid>
			</MainDialogComponent>
		</>
	);
});

MultiSelectorLink.propTypes = {
	apiRef: PropTypes.object,
	id: PropTypes.string,
	linkName: PropTypes.string,
	contextData: PropTypes.array,
	messageError: PropTypes.string,
	modalTitle: PropTypes.string,
	multiple: PropTypes.bool,
	propsId: PropTypes.string,
	field: PropTypes.string,
	fieldStateError: PropTypes.object | PropTypes.bool,
	optionsData: PropTypes.array,
	loading: PropTypes.bool,
};

AutoAlertsMultiSelector.propTypes = {
	options: PropTypes.array.isRequired,
	messageError: PropTypes.string,
	multiple: PropTypes.bool,
	loading: PropTypes.bool,
	id: PropTypes.string.isRequired,
	groupedOptions: PropTypes.array,
};

export default MultiSelectorLink;
